import { useState, useEffect, useCallback } from 'preact/hooks'
import { apiClient, InitData, tenantSiteUrl, assetUrl, udLink, seasonSignupHref } from '../../core'
import { t, intlLocale, dayNames, formatMoney, formatMoneyParts } from '../../core/i18n'
import { fmtDag, fmtTid, VAEGUR_TIDSZONE } from '../../core/tidszone'
import { parseSpotsDisplay, widgetSpotsText } from '../../core/spots-display'

interface SeasonInstructor {
  id: string
  name: string
  isPrimary: boolean
}

interface SeasonClass {
  id: string
  name?: string
  description?: string | null
  dayOfWeek?: number | null
  additionalDays?: number[]
  startTime?: string | null
  endTime?: string | null
  room?: string | null
  location?: string | null
  category?: { id: string; name: string; color: string | null } | null
  instructors?: SeasonInstructor[]
  priceDkk: number
  /** OPG-0104: «Pris efter alder» — laveste pris; den rigtige pris ses ved tilmelding. */
  hasAgePrices?: boolean
  priceFromDkk?: number
  maxParticipants: number
  registrationCount: number
  availableSpots: number
}

interface Season {
  id: string
  name: string
  slug?: string | null
  description: string | null
  imageUrl: string | null
  startDate: string
  endDate: string
  registrationOpensAt: string | null
  registrationClosesAt: string | null
  classCount: number
  classes?: SeasonClass[]
}

interface Props {
  initData: InitData
  // "cards" (default) shows summary cards. "detailed" expands each card with
  // a per-class breakdown (day, time, instructor, price, spots).
  layout?: 'cards' | 'detailed'
  // Hold-mærkat slugs from the hold-type-slugs attribute. Empty = all courses.
  holdTypeSlugs?: string[]
}


function dayName(d: number | null | undefined, locale?: string): string {
  if (d === null || d === undefined || d < 0 || d > 6) return ''
  return dayNames(locale, 'short')[d]
}

// Forløbets klokkeslæt er et vægur (@db.Time) og datoerne rene datoer (@db.Date): de vises
// i UTC (VAEGUR_TIDSZONE), som i resten af appen, ikke i den besøgendes browser (QA 27-10).
function fmtTime(iso: string | null | undefined, locale: string): string {
  if (!iso) return ''
  return fmtTid(iso, locale, VAEGUR_TIDSZONE)
}

function fmtDate(iso: string, locale: string): string {
  return fmtDag(iso, locale, VAEGUR_TIDSZONE, { day: 'numeric', month: 'long', year: 'numeric' })
}

function isRegistrationOpen(season: Season): boolean {
  const now = new Date()

  // Start gate: only enforced if explicit registrationOpensAt is set.
  // null = no start gate (always open from the start side).
  if (season.registrationOpensAt) {
    const start = new Date(season.registrationOpensAt)
    if (now < start) return false
  }

  // End gate: prefer registrationClosesAt, fall back to season.endDate.
  // If neither is set, registration stays open (no end gate).
  const endRaw = season.registrationClosesAt || season.endDate
  if (endRaw) {
    const end = new Date(endRaw)
    if (now > end) return false
  }

  return true
}

function priceFromClasses(classes: SeasonClass[] | undefined): number | null {
  if (!classes || classes.length === 0) return null
  // Med «Pris efter alder» er den laveste pris aldersgruppens (OPG-0104).
  const prices = classes.map(c => (c.hasAgePrices && typeof c.priceFromDkk === 'number' ? c.priceFromDkk : c.priceDkk)).filter(p => p > 0)
  if (prices.length === 0) return null
  return Math.min(...prices)
}

function totalCapacity(classes: SeasonClass[] | undefined): number | null {
  if (!classes || classes.length === 0) return null
  return classes.reduce((sum, c) => sum + Math.max(0, c.maxParticipants ?? 0), 0)
}

function spotsRemaining(classes: SeasonClass[] | undefined): number | null {
  if (!classes || classes.length === 0) return null
  return classes.reduce((sum, c) => sum + Math.max(0, c.availableSpots), 0)
}

/**
 * SeasonsWidget — mirrors HS-native styling for seasonal class series.
 *
 * Card layout per HS-native template:
 *   - Image at top (or gradient fallback with icon)
 *   - Season name (bold, 1.4rem)
 *   - Date range (uppercase orange label, "5. januar – 30. marts 2026")
 *   - Description (truncated to 3 lines)
 *   - Footer with "fra X kr" + spots-left indicator
 *   - Outlined "Tilmeld dig" CTA at bottom (becomes orange primary when open)
 */
export function SeasonsWidget({ initData, layout = 'cards', holdTypeSlugs = [] }: Props) {
  const str = t(initData.locale)
  const dateLocale = intlLocale(initData.locale)
  const spotsMode = parseSpotsDisplay(initData.features?.spotsDisplay)
  const [seasons, setSeasons] = useState<Season[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const slugsKey = holdTypeSlugs.join(',')
  const fetchSeasons = useCallback(async () => {
    const path = slugsKey
      ? `/api/widget/seasons?holdTypeSlugs=${encodeURIComponent(slugsKey)}`
      : '/api/widget/seasons'
    const result = await apiClient.get<Season[]>(path)
    if (result.error) {
      setError(result.error)
    } else if (result.data) {
      setSeasons(result.data)
    }
    setLoading(false)
  }, [slugsKey])

  useEffect(() => {
    fetchSeasons()
  }, [fetchSeasons])

  if (loading) {
    return (
      <div>
        <style>{styles}</style>
        <div class="hsw-season-grid">
          {[0, 1, 2].map(i => (
            <div key={i} class="hsw-season-card">
              <div class="hsw-skel hsw-skel-img" />
              <div class="hsw-season-body">
                <div class="hsw-skel hsw-skel-label" />
                <div class="hsw-skel hsw-skel-title" />
                <div class="hsw-skel hsw-skel-line" />
                <div class="hsw-skel hsw-skel-line" style={{ width: '85%' }} />
                <div class="hsw-skel hsw-skel-line" style={{ width: '60%' }} />
                <div class="hsw-skel hsw-skel-button" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <style>{styles}</style>
        <div role="alert" style={{ padding: '1rem 1.5rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--yoga-radius, 0)', color: '#b91c1c', maxWidth: '600px', margin: '0 auto' }}>
          <strong>⚠️ {error}</strong>
        </div>
      </div>
    )
  }

  if (!seasons.length) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#737373' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📆</div>
        <p>{str.noSeasons}</p>
      </div>
    )
  }

  const siteUrl = tenantSiteUrl(initData)

  return (
    <div>
      <style>{styles}</style>
      <div class={layout === 'detailed' ? 'hsw-season-grid hsw-season-grid-detailed' : 'hsw-season-grid'}>
        {seasons.map(season => {
          const open = isRegistrationOpen(season)
          const price = priceFromClasses(season.classes)
          const priceParts = price !== null ? formatMoneyParts(price, initData.locale, initData.currency) : null
          const spots = spotsRemaining(season.classes)
          const initial = season.name.charAt(0).toUpperCase()
          // Forløbets egen side (springer til tilmeldingen ved ét aktivt hold), ikke listen.
          const seasonHref = seasonSignupHref(siteUrl, season.slug)

          return (
            <div key={season.id} class={`hsw-season-card${open ? '' : ' hsw-closed'}`}>
              {season.imageUrl ? (
                <img
                  src={assetUrl(season.imageUrl)}
                  alt={season.name}
                  class="hsw-season-img"
                  loading="lazy"
                />
              ) : (
                <div class="hsw-season-img hsw-season-img-fallback">
                  <span aria-hidden="true">{initial}</span>
                </div>
              )}
              <div class="hsw-season-body">
                <div class="hsw-season-dates">
                  {fmtDate(season.startDate, dateLocale)} – {fmtDate(season.endDate, dateLocale)}
                </div>
                <h3 class="hsw-season-name">{season.name}</h3>
                {season.description && (
                  <p class="hsw-season-desc">{season.description}</p>
                )}

                {layout === 'detailed' && season.classes && season.classes.length > 0 && (
                  <ul class="hsw-season-classes">
                    {season.classes.map(c => {
                      const days = [c.dayOfWeek, ...(c.additionalDays || [])]
                        .filter((d): d is number => d !== null && d !== undefined)
                        .sort((a, b) => a - b)
                        .map(d => dayName(d, initData.locale))
                        .filter(Boolean)
                        .join(' + ')
                      const timeRange = c.startTime
                        ? `${fmtTime(c.startTime, dateLocale)}${c.endTime ? '–' + fmtTime(c.endTime, dateLocale) : ''}`
                        : ''
                      // Alle forløbets instruktører, standarden først (OPG-0128).
                      const instructors = [...(c.instructors || [])]
                        .sort((a, b) => Number(!!b.isPrimary) - Number(!!a.isPrimary))
                        .map(i => i.name)
                        .filter(Boolean)
                        .join(', ')
                      return (
                        <li key={c.id} class="hsw-season-class-row">
                          <div class="hsw-season-class-main">
                            {c.name && <span class="hsw-season-class-name">{c.name}</span>}
                            <span class="hsw-season-class-when">
                              {days}
                              {days && timeRange ? ' · ' : ''}
                              {timeRange}
                            </span>
                          </div>
                          <div class="hsw-season-class-meta">
                            {instructors && <span>{instructors}</span>}
                            {c.priceDkk > 0 && (
                              <span>
                                {c.hasAgePrices && typeof c.priceFromDkk === 'number'
                                  ? `${str.fromPrice} ${formatMoney(c.priceFromDkk, initData.locale, initData.currency)}`
                                  : formatMoney(c.priceDkk, initData.locale, initData.currency)}
                              </span>
                            )}
                            {typeof c.availableSpots === 'number' && c.maxParticipants > 0 && (() => {
                              // «Vis antal ledige pladser» (OPG-0078) gælder også her, som i de tre skema-widgets.
                              // Et fuldt hold vises som før.
                              if (c.availableSpots <= 0) {
                                return <span class="hsw-season-class-full">0/{c.maxParticipants} {str.spots}</span>
                              }
                              const tekst = widgetSpotsText(c.availableSpots, c.maxParticipants, spotsMode, str, () => `${c.availableSpots}/${c.maxParticipants} ${str.spots}`)
                              return tekst ? <span>{tekst}</span> : null
                            })()}
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                )}

                <div class="hsw-season-meta">
                  {priceParts !== null ? (
                    <span class="hsw-season-price">
                      <span class="hsw-season-price-label">{str.fromPrice}</span>
                      {priceParts.symbolFirst && <span class="hsw-season-price-suffix">{priceParts.symbol}</span>}
                      <span class="hsw-season-price-amount">{priceParts.amount}</span>
                      {!priceParts.symbolFirst && <span class="hsw-season-price-suffix">{priceParts.symbol}</span>}
                    </span>
                  ) : (
                    <span class="hsw-season-classcount">
                      {season.classCount} {season.classCount === 1 ? str.classSingular : str.classPlural}
                    </span>
                  )}
                  {open ? (
                    spots !== null && spots > 0 && spotsMode !== 'full_only' ? (
                      <span class="hsw-season-spots">
                        {widgetSpotsText(spots, totalCapacity(season.classes), spotsMode, str, () => `${spots} ${spots === 1 ? str.spotAvailable : str.spotsAvailable}`)}
                      </span>
                    ) : spots === 0 ? (
                      <span class="hsw-season-spots hsw-full">{str.soldOut}</span>
                    ) : (
                      <span class="hsw-season-spots hsw-open">{str.registrationOpen}</span>
                    )
                  ) : (
                    <span class="hsw-season-spots hsw-closed-label">{str.closed}</span>
                  )}
                </div>
                <a
                  href={open ? seasonHref : '#'}
                  {...udLink(open ? seasonHref : undefined)}
                  class="hsw-season-cta"
                  aria-disabled={!open}
                  onClick={open ? undefined : (e) => e.preventDefault()}
                >
                  {open ? str.signUp : str.registrationClosed}
                </a>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const styles = `
  .hsw-season-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
    max-width: 1200px;
    margin: 0 auto;
  }
  .hsw-season-grid-detailed {
    grid-template-columns: 1fr;
    max-width: 900px;
  }
  .hsw-season-classes {
    list-style: none;
    padding: 0;
    margin: 0 0 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }
  .hsw-season-class-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    padding: 0.6rem 0.85rem;
    background: rgba(0,0,0,0.02);
    border-radius: var(--yoga-radius, 0);
    font-size: 0.85rem;
    flex-wrap: wrap;
  }
  .hsw-season-class-main {
    display: flex;
    gap: 0.5rem;
    align-items: baseline;
    flex-wrap: wrap;
    flex: 1;
    min-width: 0;
  }
  .hsw-season-class-name {
    font-weight: 600;
    color: var(--yoga-text, #0a0a0a);
  }
  .hsw-season-class-when {
    color: var(--yoga-text-muted, #737373);
  }
  .hsw-season-class-meta {
    display: flex;
    gap: 0.85rem;
    align-items: center;
    color: var(--yoga-text-muted, #737373);
    font-size: 0.78rem;
    flex-wrap: wrap;
  }
  .hsw-season-class-full {
    color: #b91c1c;
    font-weight: 600;
  }
  .hsw-season-card {
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition: all .3s;
  }
  .hsw-season-card:hover {
    border-color: rgba(0,0,0,0.2);
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  }
  .hsw-season-card.hsw-closed {
    opacity: 0.75;
  }
  .hsw-season-img {
    display: block;
    width: 100%;
    height: 200px;
    object-fit: cover;
  }
  .hsw-season-img-fallback {
    background: linear-gradient(135deg, var(--yoga-primary, #FF6B35), var(--yoga-secondary, var(--yoga-primary-900, #1A1A2E)));
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 4rem;
    font-weight: 800;
    letter-spacing: -0.04em;
  }
  .hsw-season-body {
    padding: 1.75rem 1.5rem;
    display: flex;
    flex-direction: column;
    flex: 1;
  }
  .hsw-season-dates {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--yoga-primary, #FF6B35);
    margin-bottom: 0.6rem;
  }
  .hsw-season-name {
    font-size: 1.4rem;
    font-weight: 700;
    margin: 0 0 0.6rem;
    color: var(--yoga-text, #0a0a0a);
    letter-spacing: -0.01em;
    line-height: 1.25;
  }
  .hsw-season-desc {
    color: var(--yoga-text-muted, #525252);
    font-size: 0.9rem;
    line-height: 1.55;
    margin: 0 0 1.25rem;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .hsw-season-meta {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 0.75rem;
    margin: auto 0 1.25rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--yoga-card-border, #e5e7eb);
    flex-wrap: wrap;
  }
  .hsw-season-price {
    display: inline-flex;
    align-items: baseline;
    gap: 0.25rem;
    color: var(--yoga-text, #0a0a0a);
  }
  .hsw-season-price-label {
    font-size: 0.75rem;
    color: var(--yoga-text-muted, #737373);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 600;
  }
  .hsw-season-price-amount {
    font-size: 1.5rem;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.02em;
  }
  .hsw-season-price-suffix {
    font-size: 0.8rem;
    color: var(--yoga-text-muted, #737373);
    font-weight: 500;
  }
  .hsw-season-classcount {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--yoga-text, #0a0a0a);
  }
  .hsw-season-spots {
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: var(--yoga-text-muted, #737373);
  }
  .hsw-season-spots.hsw-open {
    color: #15803d;
  }
  .hsw-season-spots.hsw-full {
    color: #b91c1c;
  }
  .hsw-season-spots.hsw-closed-label {
    color: var(--yoga-text-muted, #737373);
  }
  .hsw-season-cta {
    display: block;
    margin-top: auto;
    padding: 0.85rem 1.25rem;
    text-align: center;
    background: transparent;
    color: var(--yoga-text, #0a0a0a);
    font-weight: 500;
    border: 1px solid #d4d4d4;
    border-radius: var(--yoga-radius, 0);
    text-decoration: none;
    font-size: 0.9rem;
    letter-spacing: 0.01em;
    transition: all .3s;
  }
  .hsw-season-cta:hover {
    background: var(--yoga-text, #0a0a0a);
    color: var(--yoga-card-bg, #fff);
    border-color: var(--yoga-text, #0a0a0a);
  }
  .hsw-season-card.hsw-closed .hsw-season-cta {
    cursor: not-allowed;
    opacity: 0.6;
  }
  .hsw-season-card.hsw-closed .hsw-season-cta:hover {
    background: transparent;
    color: var(--yoga-text, #0a0a0a);
    border-color: #d4d4d4;
  }
  /* Skeletons */
  .hsw-skel {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: hsw-shimmer 1.5s infinite;
    border-radius: 4px;
  }
  .hsw-skel-img { width: 100%; height: 200px; border-radius: 0; }
  .hsw-skel-label { height: 12px; width: 50%; margin-bottom: 0.75rem; }
  .hsw-skel-title { height: 22px; width: 75%; margin-bottom: 1rem; }
  .hsw-skel-line { height: 14px; width: 100%; margin-bottom: 0.55rem; }
  .hsw-skel-button { height: 44px; width: 100%; margin-top: 1.5rem; }
  @keyframes hsw-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  @media (max-width: 600px) {
    .hsw-season-img,
    .hsw-skel-img {
      height: 160px;
    }
    .hsw-season-body {
      padding: 1.25rem 1.1rem;
    }
  }
`
